import PaginationModel from "@/models/paginationModel";
import Delay from "@/utils/common/delay";
import { getCustomParams, getPaginationParams } from "@/utils/common/http";
import nfetch from "@/utils/events/nfetch";

export abstract class iServiceCached<T> {
    protected controllerName: string;
    protected cached: { [franquiaId: number]: T[] } = {};
    protected loadingCache = false;

    constructor(controllerName: string) {
        this.controllerName = controllerName;
    }

    public async list(
        filterKey: string,
        sortKey: string,
        sortOrder: string,
        pageIndex: number,
        pageSize: number,
        customProperties: { [key: string]: any } = {},
    ) {
        const custom = getCustomParams(customProperties);
        const pagination = getPaginationParams(filterKey, sortKey, sortOrder, pageIndex, pageSize);

        return await nfetch(`/${this.controllerName}/List?${pagination}` + custom, {
            credentials: "same-origin",
        })
            .withLoading()
            .resolveWithJSON<PaginationModel<T>>();
    }

    public async comboMethod() {
        try {
            const response = await nfetch(`/${this.controllerName}/List?`, {
                credentials: "same-origin",
            })
                .withLoading()
                .resolveWithJSON<PaginationModel<T>>(false);

            return response.list;
        } catch {}

        return [];
    }

    protected async combo(franquiaId: number, refresh = false): Promise<T[]> {
        while (this.loadingCache) await Delay(100);

        if (this.cached[franquiaId] && !refresh) {
            return this.cached[franquiaId];
        }

        this.loadingCache = true;

        try {
            const response = await this.comboMethod();

            this.cached[franquiaId] = response;

            return response;
        } catch {
        } finally {
            this.loadingCache = false;
        }
    }

    public async get(id: number) {
        return await nfetch(`/${this.controllerName}/Get?id=${id}`, {
            credentials: "same-origin",
        })
            .withLoading()
            .resolveWithJSON<T>();
    }

    public async delete(id: number, franquiaId: number) {
        const sucesso = await nfetch(`/${this.controllerName}/Delete?id=${id}`, {
            method: "post",
            credentials: "same-origin",
        })
            .withLoading()
            .resolveWithoutJSON();

        if (sucesso && this.cached[franquiaId]) {
            this.cached[franquiaId] = this.cached[franquiaId].filter(p => p["id"] != id);
        }

        return sucesso;
    }

    public async insert(model: T, franquiaId: number): Promise<Response> {
        return await this.save(model, "Insert", franquiaId);
    }

    public async update(model: T, franquiaId: number): Promise<Response> {
        return await this.save(model, "Update", franquiaId);
    }

    private async save(model: T, methodName: string, franquiaId: number): Promise<Response> {
        const response = await nfetch(`/${this.controllerName}/${methodName}`, {
            method: "post",
            credentials: "same-origin",
            headers: {
                "Content-Type": "application/json; charset=UTF-8",
            },
            body: JSON.stringify(model),
        })
            .withLoading()
            .resolveWithResponse();

        delete this.cached[franquiaId];
        return response;
    }
}
